import { Navigate, Outlet, Link } from "react-router-dom";
import { AlertTriangle, BookOpen, Terminal, FlaskConical } from "lucide-react";
import { useSession } from "@/lib/auth-client";
import Sidebar from "./Sidebar";
import Squares from "./Squares";
import { OrgProvider, useOrg } from "@/hooks/useOrg";
import { EventStreamProvider } from "@/hooks/useEventStream";

function UsageBanner() {
  const { usage } = useOrg();
  if (!usage || usage.events_limit === Infinity) return null;
  const pct = Math.round((usage.events_this_month / usage.events_limit) * 100);
  if (pct < 80) return null;

  return (
    <div className="flex items-center justify-center gap-2 bg-amber-500/10 border-b border-amber-500/20 px-4 py-1.5 text-xs text-amber-400">
      <AlertTriangle className="h-3.5 w-3.5" />
      <span>
        {pct >= 100
          ? "Monthly event limit reached."
          : `${pct}% of monthly event limit used.`}
      </span>
      <Link to="/settings?tab=billing" className="underline hover:text-amber-300">
        {pct >= 100 ? "Upgrade now" : "View billing"}
      </Link>
    </div>
  );
}

export default function Layout() {
  const { data: session, isPending } = useSession();

  if (isPending) {
    return (
      <div className="flex h-screen flex-col items-center justify-center bg-background gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-primary text-primary-foreground text-sm font-bold">
          H
        </div>
        <div className="w-32 hud-loader" />
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground animate-pulse">
          Initializing
        </span>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/sign-in" replace />;
  }

  return (
    <OrgProvider>
      <EventStreamProvider>
        <div className="flex h-screen bg-background">
          <Sidebar />
          <main className="relative flex-1 overflow-auto">
            {/* Ambient grid — subtle animated background, scoped to main content only */}
            <div className="pointer-events-none absolute inset-0 z-0">
              <Squares
                direction="diagonal"
                speed={0.15}
                squareSize={48}
                borderColor="rgba(206, 214, 255, 0.025)"
                hoverFillColor="rgba(60, 210, 165, 0.03)"
              />
            </div>
            <div className="relative z-10 flex flex-col min-h-full">
              <UsageBanner />
              <div className="mx-auto max-w-7xl w-full p-4 flex-1">
                <Outlet />
              </div>
              <footer className="border-t border-border/40">
                <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-end">
                  <div className="flex items-center gap-5">
                    {[
                      { to: "/docs", icon: BookOpen, label: "Docs" },
                      { to: "/docs/cli/overview", icon: Terminal, label: "CLI" },
                      { to: "/test", icon: FlaskConical, label: "Webhook Tester" },
                    ].map((r) => (
                      <Link
                        key={r.to}
                        to={r.to}
                        target="_blank"
                        className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-[rgba(206,214,255,0.4)] hover:text-[rgba(206,214,255,0.7)] transition-colors"
                      >
                        <r.icon className="h-3.5 w-3.5" />
                        {r.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </footer>
            </div>
          </main>
        </div>
      </EventStreamProvider>
    </OrgProvider>
  );
}
